import React, { useContext, useEffect, useState } from "react";
import moment from "moment-timezone";
import _ from "lodash";
import Draggable from "react-draggable";
import { styled } from "@mui/system";

import { Divider, IconButton, Paper, Typography } from "@mui/material";

import { useTheme } from "@mui/material/styles";

import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import InfoIcon from "@mui/icons-material/Info";
import CloudDownloadIcon from "@mui/icons-material/CloudDownload";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

import ModelContext from "../../contexts/Model/ModelContext";

const DraggableContainer = styled("div")(({ dragging }) => ({
  position: "absolute",
  cursor: dragging ? "grabbing" : "grab",
}));

function PopupTools(props) {
  const theme = useTheme();

  const { models } = useContext(ModelContext);

  const {
    zoom,
    imageData,
    handleResetZoom,
    handleZoomIn,
    handleZoomOut,
    handleClose,
    handleShowUploadDialog,
  } = props;

  const [draggingTools, setDraggingTools] = useState(false);

  const [showInfoPanel, setShowInfoPanel] = useState(
    isProcessedImage(imageData)
  );

  const [modelName, setModelName] = useState("");

  const handleDownload = () => {
    const fileUrl = imageData.file;

    const link = document.createElement("a");
    link.href = fileUrl;
    link.download = "bcip_ml_image.jpeg";

    link.click();
  };

  useEffect(() => {
    const modelId = _.get(imageData, "model", "");
    const model = _.find(models, { _id: modelId });

    if (!model || !model.displayName) {
      setModelName("Unknown");
      return;
    }

    setModelName(model.displayName);
  }, []);

  return (
    <Draggable
      onDrag={() => setDraggingTools(true)}
      onStop={() => setDraggingTools(false)}
    >
      <DraggableContainer
        dragging={draggingTools}
        style={{
          position: "fixed",
          bottom: "5%",
          margin: "auto",
        }}
      >
        <Paper
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          {showInfoPanel && (
            <div
              style={{
                margin: theme.spacing(),
                display: "flex",
                flexDirection: "column",
              }}
            >
              {imageData.model && (
                <Typography variant="caption">Model: {modelName}</Typography>
              )}
              {imageData.counts && (
                <Typography variant="caption">
                  Number of Containers: {getContainersForImageData(imageData)}
                </Typography>
              )}
              {imageData.createdAt && (
                <Typography variant="caption">
                  Date Processed: {formatDateForImageData(imageData)}
                </Typography>
              )}
              {!_.isNil(imageData.duration) && (
                <Typography variant="caption">
                  Time to process: {`${_.round(imageData.duration, 2)}s`}
                </Typography>
              )}
            </div>
          )}
          <Divider variant="middle" />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              marginLeft: theme.spacing(4),
              marginRight: theme.spacing(4),
            }}
          >
            {isProcessedImage(imageData) && (
              <IconButton
                onClick={() => setShowInfoPanel(!showInfoPanel)}
                // onTouchStart={() => setShowInfoPanel(!showInfoPanel)}
                style={{ padding: 0, margin: theme.spacing(1) }}
              >
                <InfoIcon />
              </IconButton>
            )}
            <IconButton
              disabled={zoom >= 2}
              onClick={handleZoomIn}
              // onTouchStart={handleZoomIn}
              style={{ padding: 0, margin: theme.spacing(1) }}
            >
              <ZoomInIcon />
            </IconButton>
            <IconButton
              disabled={zoom <= 0.5}
              onClick={handleZoomOut}
              // onTouchStart={handleZoomOut}
              style={{ padding: 0, margin: theme.spacing(1) }}
            >
              <ZoomOutIcon />
            </IconButton>
            <IconButton
              disabled={zoom === 1}
              onClick={handleResetZoom}
              // onTouchStart={handleResetZoom}
              style={{ padding: 0, margin: theme.spacing(1) }}
            >
              <FullscreenIcon />
            </IconButton>
            {isUploadedImage(imageData) && (
              <IconButton
                onClick={handleShowUploadDialog}
                // onTouchStart={handleDownload}
                style={{
                  padding: 0,
                  margin: theme.spacing(1),
                  marginLeft: theme.spacing(2),
                }}
              >
                <CloudUploadIcon />
              </IconButton>
            )}
            <IconButton
              onClick={handleDownload}
              // onTouchStart={handleDownload}
              style={{
                padding: 0,
                margin: theme.spacing(1),
                marginLeft: theme.spacing(2),
              }}
            >
              <CloudDownloadIcon />
            </IconButton>
            <IconButton
              onClick={handleClose}
              // onTouchStart={handleClose}
              style={{
                padding: 0,
                margin: theme.spacing(1),
              }}
            >
              <CloseIcon />
            </IconButton>
            <DragIndicatorIcon
              style={{
                color: theme.palette.grey[400],
                fontSize: "1.5rem",
                cursor: "default",
                backgroundColor: "transparent",
                margin: theme.spacing(0.3),
                cursor: "grab",
              }}
            />
          </div>
        </Paper>
      </DraggableContainer>
    </Draggable>
  );
}

export default PopupTools;

function getContainersForImageData(imageData) {
  const counts = imageData.counts;
  return _.reduce(
    counts,
    (totalContainers, count) => totalContainers + count.amount || 0,
    0
  );
}

function formatDateForImageData(imageData) {
  const date = imageData.createdAt;
  return moment(date).tz(process.env.REACT_APP_TIMEZONE).format("MMM DD, YYYY");
}

function isProcessedImage(imageData) {
  return imageData.type === "processed";
}

function isUploadedImage(imageData) {
  return imageData.type === "uploaded";
}
